import { PropertyTypes } from "epsm-api-types";
import { useEffect, useMemo, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	DEFAULT_BIGNUMBER_OPTIONS,
	DEFAULT_PERCENT_OPTIONS,
	formatValue,
	formatValueBigNumber,
} from "@/components/widgets/helpers/FormattingHelpers";
import {
	COMPONENT_CONTAINER_CLASSES,
	EMPTY_VALUE,
	MIN_MAX_COLUMN_CLASSES,
} from "@/components/widgets/Revision/config";
import {
	type ColumnValues,
	RevisionDataSource,
	RevisionDataValueMode,
	RevisionDataViewMode,
	type RevisionExt,
	type TableColumn,
} from "@/components/widgets/Revision/types";
import { cn } from "@/lib/utils";
import { KEY } from "@/shared/constants/keyboard";
import { valueColor } from "../../../utils/RevisionTableUtils";
import { TableWatermark } from "../TableWatermark";
import { getPeriodName } from "./lib/helpers";

type UpdatedValues = {
	[k: string]: Partial<TableColumn>;
};

const days: Array<keyof ColumnValues> = [
	"0d",
	"2d",
	"3d",
	"4d",
	"7d",
	"30d",
	"60d",
	"90d",
];

export const RevisionData = ({
	data,
	revisionDataViewMode,
	revisionDataValueMode,
	revisionDataSource,
	onStateChange,
}: {
	data: RevisionExt;
	revisionDataViewMode: RevisionDataViewMode;
	revisionDataValueMode: RevisionDataValueMode;
	revisionDataSource: RevisionDataSource;
	onStateChange?: OnStateChange;
}) => {
	const [activeRevisionType, setActiveRevisionType] =
		useState<RevisionDataValueMode>(revisionDataValueMode);
	const [activeRsType, setActiveRsType] =
		useState<RevisionDataViewMode>(revisionDataViewMode);
	const [activeSource, setActiveSource] =
		useState<RevisionDataSource>(revisionDataSource);
	const [valueFormat, setValueFormat] = useState<Intl.NumberFormatOptions>(
		DEFAULT_PERCENT_OPTIONS,
	);

	const hasZcData = Boolean(data.mt?.ns || data.dp?.ns || data.ex?.ns);
	const hasFhData = Boolean(data.mt?.fh || data.dp?.fh || data.ex?.fh);

	// biome-ignore lint: correctness/useExhaustiveDependencies: don't need onStateChange
	useEffect(() => {
		if (!hasZcData && activeSource === RevisionDataSource.ZACKS) {
			setActiveSource(RevisionDataSource.OTHER);
			onStateChange?.({ revisionDataSource: RevisionDataSource.OTHER });
		}
		if (!hasFhData && activeSource === RevisionDataSource.OTHER) {
			setActiveSource(RevisionDataSource.ZACKS);
			onStateChange?.({ revisionDataSource: RevisionDataSource.ZACKS });
		}
	}, [hasFhData, hasZcData, activeSource]);

	const [selectedValue, updatedDpValues] = useMemo(() => {
		const selectedMap = new Map(
			(data?.mt?.[activeSource]?.[activeRsType]?.value || []).map((item) => [
				getPeriodName(item),
				item,
			]),
		);
		const dpMap = new Map(
			(data?.dp?.[activeSource]?.ee || []).map((item) => [
				getPeriodName(item),
				item,
			]),
		);

		const allKeys = new Set([...selectedMap.keys(), ...dpMap.keys()]);

		const selectedValue = Object.fromEntries(selectedMap);
		const updatedDpValues = Object.fromEntries(
			Array.from(allKeys, (key) => [
				key,
				dpMap.get(key) ?? {
					...{
						dispersion: undefined,
					},
				},
			]),
		);

		return [selectedValue, updatedDpValues];
	}, [data, activeSource, activeRsType]);

	const valueFormatHandler = () => {
		const nextActiveRevisionType =
			activeRevisionType === RevisionDataValueMode.CHANGES
				? RevisionDataValueMode.VALUES
				: RevisionDataValueMode.CHANGES;

		const nextValueFormat =
			nextActiveRevisionType === RevisionDataValueMode.CHANGES
				? DEFAULT_PERCENT_OPTIONS
				: DEFAULT_BIGNUMBER_OPTIONS;

		setActiveRevisionType(nextActiveRevisionType);
		setValueFormat(nextValueFormat);
		onStateChange?.({ revisionDataValueMode: nextActiveRevisionType });
	};

	const renderRevisionHeaderRows = (type: string) => {
		const revisionScore =
			activeRsType === RevisionDataViewMode.EPS_RS ? "rs" : "rss";

		return days.map((day) => (
			<tr key={`epsRsDay${type}${day}`}>
				<td
					className={`border-r-2 border-gray text-white text-center ${MIN_MAX_COLUMN_CLASSES}`}
				>
					{formatValueBigNumber(
						data?.ex?.[activeSource]?.[revisionScore]?.scores_info?.[day],
						EMPTY_VALUE,
					)}
				</td>
				<td
					className={cn("!text-light-gray text-center", MIN_MAX_COLUMN_CLASSES)}
				>
					{day === "0d" ? "Latest" : day}
				</td>
			</tr>
		));
	};

	const renderRevisionRows = (updatedValues: UpdatedValues, type: string) => {
		const lastDayNumber = days[days.length - 1];
		const isChanges = activeRevisionType === RevisionDataValueMode.CHANGES;

		return days.map((day) => (
			<tr key={`epsRsDay${type}${day}`}>
				{Object.keys(updatedValues).map((value) => {
					const dayValue = updatedValues[value]?.[activeRevisionType]?.[day];
					const colorClass =
						!isChanges || dayValue === undefined ? "" : valueColor(dayValue);
					const otherClasses =
						day === lastDayNumber ? "border-b-2 border-gray" : "";

					return (
						<td
							key={`epsRs${type}${day}${value}`}
							className={`${colorClass} ${otherClasses}`}
						>
							{formatValue(
								updatedValues[value]?.[activeRevisionType]?.[day],
								PropertyTypes.NUMBER,
								valueFormat,
								EMPTY_VALUE,
							)}
						</td>
					);
				})}
			</tr>
		));
	};

	return (
		<div className={COMPONENT_CONTAINER_CLASSES}>
			<h3 className="text-light-gray text-lg">Revision Data</h3>
			<div className="w-full overflow-y-auto flex flex-row">
				<table
					className="bg-background border-gray border-2 text-left sticky left-0"
					data-testid="revision-table-header"
				>
					<tbody>
						<tr className="border-b-2 border-gray">
							<td
								className={`border-r-2 border-gray text-gold font-bold ${MIN_MAX_COLUMN_CLASSES}`}
								onClick={() => {
									const next =
										activeRsType === RevisionDataViewMode.EPS_RS
											? RevisionDataViewMode.SALES_RS
											: RevisionDataViewMode.EPS_RS;
									setActiveRsType(next);
									onStateChange?.({ revisionDataViewMode: next });
								}}
								onKeyDown={(event) => {
									if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
										const next =
											activeRsType === RevisionDataViewMode.EPS_RS
												? RevisionDataViewMode.SALES_RS
												: RevisionDataViewMode.EPS_RS;
										setActiveRsType(next);
										onStateChange?.({ revisionDataViewMode: next });
									}
								}}
							>
								{activeRsType === "ee" ? "EPS RS" : "Sales RS"}
							</td>
							<td
								className={`text-gold font-bold ${MIN_MAX_COLUMN_CLASSES}`}
								onClick={valueFormatHandler}
								onKeyDown={(event) => {
									if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
										valueFormatHandler();
									}
								}}
							>
								{activeRevisionType === RevisionDataValueMode.VALUES
									? "Value"
									: "Change"}
							</td>
						</tr>
						<tr>
							<Tooltip>
								<TooltipTrigger asChild>
									<td
										className={`text-gold font-bold border-r-2 border-gray ${MIN_MAX_COLUMN_CLASSES}`}
										onClick={() => {
											if (!hasFhData || !hasZcData) return;
											const next =
												activeSource === RevisionDataSource.ZACKS
													? RevisionDataSource.OTHER
													: RevisionDataSource.ZACKS;
											setActiveSource(next);
											onStateChange?.({ revisionDataSource: next });
										}}
										onKeyDown={(event) => {
											if (!hasFhData || !hasZcData) return;
											if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
												const next =
													activeSource === RevisionDataSource.ZACKS
														? RevisionDataSource.OTHER
														: RevisionDataSource.ZACKS;
												setActiveSource(next);
												onStateChange?.({ revisionDataSource: next });
											}
										}}
									>
										{activeSource === RevisionDataSource.ZACKS
											? "Source 1"
											: "Source 2"}
									</td>
								</TooltipTrigger>
								<TooltipContent>
									{activeSource === RevisionDataSource.ZACKS
										? "Zacks"
										: "Other"}
								</TooltipContent>
							</Tooltip>

							<td className={MIN_MAX_COLUMN_CLASSES}>Analysts</td>
						</tr>
						<tr>
							<td
								className={`border-r-2 border-gray ${MIN_MAX_COLUMN_CLASSES}`}
							>
								&nbsp;
							</td>
							<td className="border-b-2 border-gray">Dispersion</td>
						</tr>
						{renderRevisionHeaderRows(activeRsType)}
					</tbody>
				</table>

				<table
					className="w-full text-light-gray border-separate border-spacing-0 text-right"
					data-testid="revision-table-body"
				>
					<thead>
						<tr>
							{Object.keys(selectedValue).map((value) => (
								<th
									className={cn(
										"font-normal border-y-2 border-gray text-center",
										MIN_MAX_COLUMN_CLASSES,
									)}
									key={`EPS${value}`}
								>
									{value}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							{Object.keys(selectedValue).map((value) => (
								<td key={`AI${value}`}>
									{formatValueBigNumber(
										selectedValue[value].number_of_analysts,
										EMPTY_VALUE,
									)}
								</td>
							))}
						</tr>
						<tr>
							{Object.keys(updatedDpValues).map((value) => (
								<td key={`DP${value}`} className="border-b-2 border-gray">
									{formatValueBigNumber(
										updatedDpValues[value].dispersion,
										EMPTY_VALUE,
									)}
								</td>
							))}
						</tr>
						{renderRevisionRows(selectedValue, activeRsType)}
					</tbody>
				</table>
			</div>
			<TableWatermark />
		</div>
	);
};
